import { useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import ResaleEmptyState from '../components/ResaleEmptyState';
import ResaleSummaryMobile from '../components/ResaleSummaryMobile';
import ResaleTicketCard from '../components/ResaleTicketCard';
import { useBreadCrumbsContext } from '../hooks/contexts/breadCrumb.context';
import { useI18nContext } from '../hooks/contexts/i18n.context';
import type { ResaleListingItem } from '../hooks/contexts/resale-listing.context';
import { useResaleUserContext } from '../hooks/contexts/resale-user.context';
import { useResaleContext } from '../hooks/contexts/resale.context';
import { useResaleEventContext } from '../hooks/contexts/resaleEvent.context';
import { useUserContext } from '../hooks/contexts/user.context';
import useIsMobile from '../hooks/useIsMobile';
import { useResaleTracking } from '../hooks/useResaleTracking';
import { useFastPassOfferContext } from '../providers/FastPassOfferProvider';
import * as Styles from '../styles/pages/resale';
import type { ResaleData } from '../types/resale';
import { logger } from '../utils/logger';

function mapListingItemToResaleData(item: ResaleListingItem): ResaleData {
  const basePrice = item.saleValue ?? item.finalPrice;
  return {
    id : 0,
    uuid : item.uuid,
    saleTicketId : item.ticket.id ?? 0,
    userId : item.sellerId ?? 0,
    status : 'active',
    saleValue : basePrice,
    originalPrice : basePrice,
    processingFee : 0,
    fastpassFee : 0,
    netAmount : basePrice,
    autoSale : false,
    fastpassEnabled : item.fastpassEnabled,
    reactivationCount : 0,
    url : '',
    resaleUrl : '',
    canDeactivate : false,
    activatedAt : '',
    deactivatedAt : null,
    soldAt : null,
    buyerUserId : null,
    createdAt : '',
    updatedAt : '',
    ticket : {
      id : item.ticket.id ?? 0,
      code : '',
      sequence : '',
      originalPrice : basePrice,
      tax : 0,
      guestType : {
        id : item.ticket.guestType.id,
        name : item.ticket.guestType.name,
        type : item.ticket.guestType.type ?? '',
      },
    },
    event : {
      id : item.event.id ?? 0,
      title : item.event.title ?? '',
      from : item.event.dates?.from ?? null,
      to : item.event.dates?.to ?? null,
      status : '',
      description : '',
      timezone : item.event.dates?.timezone ?? '',
      link : '',
      type : '',
      dates : {
        from : item.event.dates?.from ?? '',
        to : item.event.dates?.to ?? '',
        timezone : item.event.dates?.timezone ?? '',
      },
      venue : {
        id : 0,
        name : '',
        street : '',
        city : '',
        state : '',
        country : '',
        complement : null,
        crossstreet : null,
        zipcode : '',
        latitude : 0,
        longitude : 0,
      },
    },
    isActive : true,
    isSold : false,
    isDeactivated : false,
    withdrawal : null,
  };
}

type ResaleParams = {
  resaleId: string;
};

const ResalePage = () => {
  const { resaleId } = useParams<ResaleParams>();
  const location = useLocation();
  const { translate } = useI18nContext();
  const { user, authenticatedUser } = useUserContext();
  const { setUser : setResaleUser } = useResaleUserContext();
  const { resaleData, loading, hasError : resaleHasError, errorType, getResaleById } = useResaleContext();
  const { event, fetchEventById, hasError : eventHasError } = useResaleEventContext();
  const isMobile = useIsMobile();
  const { isResalePaymentPage } = useBreadCrumbsContext();
  const { resaleTracking } = useResaleTracking();
  const { offerId, finalPrice, isValid, loading : offerLoading } = useFastPassOfferContext();
  const isOfferResolving = !!offerId && offerLoading;
  // Caminho 2: se veio da listing page via waitlist, usa o finalPrice passado no navigate state
  // como fallback quando não há offerId resolvido (Caminho 1). Garante preço consistente
  // entre listing → detail → checkout.
  type ListingNavState = {
    overridePrice?: number; source?: string; waitlistId?: string; listingItem?: ResaleListingItem
  };
  const listingState = location.state as ListingNavState | null;
  const listingOverridePrice = listingState?.overridePrice;
  const listingSource = listingState?.source;
  const listingWaitlistId = listingState?.waitlistId;
  const listingItem = listingState?.listingItem;
  // Hydrate from listing nav state so detail page renders immediately without waiting for API
  const hydratedFromNav = listingItem ? mapListingItemToResaleData(listingItem) : null;
  const effectiveResaleData: ResaleData | null = resaleData ?? hydratedFromNav;
  const displayPrice = isValid && finalPrice !== null
    ? finalPrice
    : (listingOverridePrice ?? undefined);
  const syncedUserIdRef = useRef<number | null>(null);
  const hasTrackedPageView = useRef(false);

  /**
   * Sync UserContext (cookies) to ResaleUserContext (localStorage)
   * This ensures user data persists when navigating to resale-payment
   *
   * Contract: Only syncs when user ID changes (tracked via ref)
   * - User token is passed to setResaleUser but doesn't trigger re-sync
   * - This prevents unnecessary updates when only token changes
   * - The ref guard ensures we sync once per unique user ID
   */
  useEffect(() => {
    if (user && user.id !== syncedUserIdRef.current) {
      setResaleUser(user, user.token);
      syncedUserIdRef.current = user.id;
    }
  }, [user?.id, setResaleUser]);

  // Load resale data on mount
  useEffect(() => {
    if (resaleId) {
      getResaleById(resaleId);
    } else {
      logger.warn('No resaleId found in URL params');
    }
    // Note: getResaleById omitted from deps to prevent infinite loop (unstable context function)
  }, [resaleId]);

  // Fetch event data — use listing item's event id as fallback so fetch starts immediately
  useEffect(() => {
    const eventId = resaleData?.event?.id ?? listingItem?.event?.id;
    if (eventId) {
      fetchEventById(eventId);
    }
    // Note: fetchEventById omitted from deps to prevent infinite loop (unstable context function)
  }, [resaleData?.event?.id, listingItem?.event?.id]);

  // Track page view and data load success when both resaleData and event are available
  useEffect(() => {
    if (resaleData && event && !hasTrackedPageView.current) {
      hasTrackedPageView.current = true;

      // Track data load success
      resaleTracking.trackDataLoadSuccess(resaleData, event, user, authenticatedUser || false);

      // Track page view
      resaleTracking.trackPageView(resaleData, event, user, authenticatedUser || false);

      logger.info('[ResalePage] Tracked page view and data load success');
    }
  }, [resaleData, event, user, authenticatedUser, resaleTracking]);

  // Track data load failed when resale fetch fails
  useEffect(() => {
    if (resaleHasError && resaleId) {
      resaleTracking.trackDataLoadFailed(
        resaleId,
        'Failed to load resale data',
        'resale_not_found',
        'Resale API returned error'
      );

      logger.warn('[ResalePage] Tracked data load failed - resale not found');
    }
  }, [resaleHasError, resaleId, resaleTracking]);

  // Track data load failed when event fetch fails
  useEffect(() => {
    if (eventHasError && resaleId) {
      resaleTracking.trackDataLoadFailed(
        resaleId,
        'Failed to load event data',
        'event_not_found',
        'Event API returned error'
      );

      logger.warn('[ResalePage] Tracked data load failed - event not found');
    }
  }, [eventHasError, resaleId, resaleTracking]);

  // Skip loading when listing data is available — API runs in background to verify availability
  if (loading && !effectiveResaleData) {
    return null;
  }

  // Check if resale or event fetch returned an error
  if (resaleHasError || eventHasError) {
    return <ResaleEmptyState errorType={errorType} />;
  }

  // If no error but still no data, return null (shouldn't happen)
  if (!effectiveResaleData) {
    return null;
  }

  // Show ownResale empty state when navigated back from payment page
  if (location.state?.errorType === 'ownResale') {
    return <ResaleEmptyState errorType="ownResale" />;
  }

  // Check if resale is available for purchase (uses API data once loaded; optimistic true from listing)
  const { isActive, isSold, isDeactivated } = effectiveResaleData;
  const isResaleAvailable = isActive && !isSold && !isDeactivated;

  return isResaleAvailable ? (
    <Styles.WrapperResale id="resale-page-wrapper">
      {authenticatedUser && (
        <>
          <Styles.SectionTitle id="resale-buyer-data-title">
            {translate(['RESALE', 'BUYER_DATA'])}
          </Styles.SectionTitle>

          <ResaleTicketCard
            resaleData={effectiveResaleData}
            user={user}
            showBuyerInfo={!!user}
            overridePrice={displayPrice}
          />
        </>
      )}

      {isMobile && !isResalePaymentPage && (
        <ResaleSummaryMobile
          overridePrice={displayPrice}
          offerPending={isOfferResolving}
          source={listingSource}
          waitlistId={listingWaitlistId}
        />
      )}
    </Styles.WrapperResale>
  ) : (
    <ResaleEmptyState
      isActive={effectiveResaleData.isActive}
      isSold={effectiveResaleData.isSold}
      isDeactivated={effectiveResaleData.isDeactivated}
      status={effectiveResaleData.status}
      errorType={errorType}
    />
  );
};

export default ResalePage;
